import { Tabs } from 'antd';
import {
  camelCase,
  includes,
  isEmpty,
  map,
  replace,
  toLower,
  trim,
  upperFirst,
} from 'lodash';
import React, { useState } from 'react';
import projectResource from '../../../locales/th/project.json';
import { CustomTab } from '../../latestPost/LatestPost.style';
import ShowMore from '../../showMore/ShowMore';
import SectionTitle from '../sectionTitle/SectionTitle';
import {
  getRoomDetailConfig,
  showPublicUtilitiesInformationFields,
  showRoomInformationFields,
  showSecuritesInformationFields,
} from './other-project-detail-config';
import {
  Divider,
  OtherProjectDetailContent,
  OtherProjectDetailContentWrapper,
  OtherProjectDetailWrapper,
  ProjectAttribute,
  ProjectAttributeData,
  ProjectAttributeTitle,
} from './OtherProjectDetail.style';
import { TransformedProjectNetDataType } from '../../../common/helpers/data/transformProjectnetProjectData';

const TabPane = Tabs.TabPane;

interface OtherProjectDetailProps {
  projectData: TransformedProjectNetDataType & {
    securities?: { value: string }[];
  };
  window?: Window;
}
const OtherProjectDetail: React.FC<OtherProjectDetailProps> = ({
  projectData,
}) => {
  const {
    roomProfile = { equipment: {}, sink: {}, stove_hood: {} },
    facilities = [],
    securities = [],
  } = projectData;
  const { equipment = {}, sink = {}, stove_hood = {} } = roomProfile;

  const roomDetailsConf = getRoomDetailConfig(
    roomProfile,
    equipment,
    sink,
    stove_hood,
  );

  const roomInformation = showRoomInformationFields(roomDetailsConf);
  const publicUtilitiesInformation =
    showPublicUtilitiesInformationFields(facilities);
  const securitiesInformation = showSecuritesInformationFields(securities);

  const [roomDetails] = useState(roomDetailsConf);
  const [publicUtilities] = useState(facilities);
  const [securitiesState] = useState(securities);
  const [showRoomDetails, setShowRoomDetails] = useState(
    roomInformation.setShowRoomDetails,
  );
  const [showPublicUtilities, setShowPublicUtilities] = useState(
    publicUtilitiesInformation.setShowPublicUtilities,
  );
  const [showSecurities, setShowSecurities] = useState(
    securitiesInformation.setShowSecurities,
  );
  const [isShowMoreRoomDetails, setIsShowMoreRoomDetails] = useState(
    roomInformation.setIsShowMoreRoomDetails,
  );
  const [isShowMorePublicUtilities, setIsShowMorePublicUtilities] = useState(
    publicUtilitiesInformation.setIsShowMorePublicUtilities,
  );
  const [isShowMoreSecurities, setIsShowMoreSecurities] = useState(
    securitiesInformation.setIsShowMoreSecurities,
  );
  const [willTranslate] = useState(['include', 'no information']);

  const showRoomDetailsMoreInfo = () => {
    setShowRoomDetails(roomDetails);
    setIsShowMoreRoomDetails(false);
  };

  const showPublicUtilitiesMoreInfo = () => {
    setShowPublicUtilities(publicUtilities);
    setIsShowMorePublicUtilities(false);
  };

  const showSecuritiesMoreInfo = () => {
    setShowSecurities(securitiesState);
    setIsShowMoreSecurities(false);
  };

  const setFieldLabel = (data: { value: string }) => {
    const arr = ['Y', 'N', 'O'];
    if (includes(arr, data.value)) {
      let value = 'No Information';
      if (data.value === 'Y') {
        value = 'Include';
      }
      if (data.value === 'N') {
        value = 'Not Include';
      }

      return value;
    } else {
      return data.value;
    }
  };

  const formatLabel = (item: string) => {
    return upperFirst(replace(item, new RegExp('_', 'g'), ' '));
  };

  const renderRoomDetail = () => {
    const showMoreText = projectResource.showMore;

    return (
      <OtherProjectDetailContentWrapper>
        <OtherProjectDetailContent>
          {showRoomDetails.map((roomDetail, index) => {
            return (
              !isEmpty(roomDetail.value) &&
              toLower(roomDetail.value) !== 'none' && (
                <ProjectAttribute key={index}>
                  <ProjectAttributeTitle data-test={'room-detail-title'}>
                    {roomDetail.label}
                  </ProjectAttributeTitle>
                  <ProjectAttributeData
                    data-test={'room-detail-attribute-data'}
                  >
                    {roomDetail.value}
                  </ProjectAttributeData>
                </ProjectAttribute>
              )
            );
          })}
        </OtherProjectDetailContent>
        {isShowMoreRoomDetails && (
          <ShowMore
            isUsedInProjectPage={true}
            showMoreText={showMoreText}
            className="detail-show-more"
            showMoreInfo={() => showRoomDetailsMoreInfo()}
          />
        )}
      </OtherProjectDetailContentWrapper>
    );
  };

  const renderPublicUtilities = () => {
    const showMoreText = projectResource.showMore;

    return (
      <OtherProjectDetailContentWrapper>
        <OtherProjectDetailContent>
          {map(showPublicUtilities, (item, i) => {
            const labelValue = camelCase(formatLabel(item.label));
            const itemValue = setFieldLabel(item);
            return (
              item.value !== 'N' &&
              trim(item.value) && (
                <ProjectAttribute key={i}>
                  <ProjectAttributeTitle data-test={'public-utilities-title'}>
                    {
                      projectResource.otherProjectDetail.publicUtilities[
                        labelValue as keyof typeof projectResource.otherProjectDetail.publicUtilities
                      ]
                    }
                  </ProjectAttributeTitle>
                  <ProjectAttributeData
                    data-test={'public-utilities-attribute-data'}
                  >
                    {includes(willTranslate, toLower(itemValue))
                      ? projectResource.projectInformation[
                          camelCase(
                            itemValue,
                          ) as keyof typeof projectResource.projectInformation
                        ]
                      : itemValue}
                  </ProjectAttributeData>
                </ProjectAttribute>
              )
            );
          })}
        </OtherProjectDetailContent>
        {isShowMorePublicUtilities && (
          <ShowMore
            isUsedInProjectPage={true}
            showMoreText={showMoreText}
            className="detail-show-more"
            showMoreInfo={() => showPublicUtilitiesMoreInfo()}
          />
        )}
      </OtherProjectDetailContentWrapper>
    );
  };

  const renderSecurities = () => {
    const showMoreText = projectResource.showMore;

    return (
      <OtherProjectDetailContentWrapper>
        <OtherProjectDetailContent>
          {map(showSecurities, (item, i) => {
            const labelValue = camelCase(formatLabel(item.label));
            const itemValue = setFieldLabel(item);
            return (
              item.value !== 'N' &&
              trim(item.value) && (
                <ProjectAttribute key={i}>
                  <ProjectAttributeTitle data-test={'securities-title'}>
                    {
                      projectResource.otherProjectDetail.security[
                        labelValue as keyof typeof projectResource.otherProjectDetail.security
                      ]
                    }
                  </ProjectAttributeTitle>
                  <ProjectAttributeData data-test={'securities-attribute-data'}>
                    {includes(willTranslate, toLower(itemValue))
                      ? projectResource.projectInformation[
                          camelCase(
                            itemValue,
                          ) as keyof typeof projectResource.projectInformation
                        ]
                      : itemValue}
                  </ProjectAttributeData>
                </ProjectAttribute>
              )
            );
          })}
        </OtherProjectDetailContent>
        {isShowMoreSecurities && (
          <ShowMore
            isUsedInProjectPage={true}
            showMoreText={showMoreText}
            className="detail-show-more"
            showMoreInfo={() => showSecuritiesMoreInfo()}
          />
        )}
      </OtherProjectDetailContentWrapper>
    );
  };

  const sectionTitle = projectResource.otherProjectDetail.title;
  const roomDetailsTitle = projectResource.otherProjectDetail.roomDetails.title;
  const publicUtilitiesTitle =
    projectResource.otherProjectDetail.publicUtilities.title;
  const securityTitle = projectResource.otherProjectDetail.security.title;
  const [activeTab, setActiveTab] = useState('1');

  return (
    !(
      isEmpty(showRoomDetails) &&
      isEmpty(showPublicUtilities) &&
      isEmpty(showSecurities)
    ) && (
      <>
        <Divider />
        <OtherProjectDetailWrapper>
          <SectionTitle isOtherProjectDetail>{sectionTitle}</SectionTitle>
          <Tabs
            activeKey={activeTab}
            onChange={(activeKey) => setActiveTab(activeKey)}
          >
            {!isEmpty(showRoomDetails) && (
              <TabPane
                tab={
                  <CustomTab active={activeTab === '1'}>
                    {roomDetailsTitle}
                  </CustomTab>
                }
                key="1"
              >
                {renderRoomDetail()}
              </TabPane>
            )}
            {!isEmpty(showPublicUtilities) && (
              <TabPane
                tab={
                  <CustomTab active={activeTab === '2'}>
                    {publicUtilitiesTitle}
                  </CustomTab>
                }
                key="2"
              >
                {renderPublicUtilities()}
              </TabPane>
            )}
            {!isEmpty(showSecurities) && (
              <TabPane
                tab={
                  <CustomTab active={activeTab === '3'}>
                    {securityTitle}
                  </CustomTab>
                }
                key="3"
              >
                {renderSecurities()}
              </TabPane>
            )}
          </Tabs>
        </OtherProjectDetailWrapper>
      </>
    )
  );
};

export default OtherProjectDetail;
